import { ApiError } from "@divespots/contracts";

/** Codes from the contract plus three client-side ones. */
export type ApiErrorCode =
  ApiError["error"]["code"] | "network" | "invalid_response" | "not_implemented";

/** The single error type thrown by the API client. */
export class ApiRequestError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly details?: unknown;

  constructor(code: ApiErrorCode, message: string, status = 0, details?: unknown) {
    super(message);
    this.name = "ApiRequestError";
    this.code = code;
    this.status = status;
    this.details = details;
  }

  get isNotFound(): boolean {
    return this.code === "not_found";
  }
}

export function isApiRequestError(e: unknown): e is ApiRequestError {
  return e instanceof ApiRequestError;
}

/** Turns an error response body into a typed error; non-contract bodies become `invalid_response`. */
export function errorFromResponse(status: number, json: unknown): ApiRequestError {
  const parsed = ApiError.safeParse(json);
  if (parsed.success) {
    const { code, message, details } = parsed.data.error;
    return new ApiRequestError(code, message, status, details);
  }
  // a proxy in front of the API may answer 413 with its own page: still "too large", not garbage
  if (status === 413) {
    return new ApiRequestError("payload_too_large", "request body too large", status);
  }
  return new ApiRequestError(
    "invalid_response",
    `HTTP ${status} without a valid error body`,
    status,
    parsed.error,
  );
}
